import React, { ReactNode } from 'react';
import { useButtonPermissions } from '../hooks/useButtonPermissions';
import { useImpersonation } from '../contexts/ImpersonationContext';

export type ButtonType = 'save' | 'edit' | 'delete' | 'submit';

interface PermissionAwareButtonProps {
  /**
   * Type of button to check permissions for
   * - 'save': Button - Save permissions
   * - 'edit': Button - Save permissions (same as save)
   * - 'delete': Button - Delete permissions
   * - 'submit': Button - Submit permissions
   */
  buttonType: ButtonType;

  /**
   * Current request stage for permission checking
   */
  requestStage?: string;

  /**
   * Optional request ID for debugging
   */
  requestId?: string;

  /**
   * Button content to render when permission is granted
   * This is completely customizable - can be any React component
   */
  children: ReactNode;

  /**
   * Component to show when loading permissions (optional)
   * If not provided, children will be shown during loading
   */
  loadingComponent?: ReactNode;

  /**
   * Component to show when user has no permission (optional)
   * If not provided, nothing will be rendered (button hidden)
   */
  noPermissionComponent?: ReactNode;
}

/**
 * Permission-aware button wrapper component
 *
 * Checks button permissions from config files and renders children only if user has permission
 * - W permission = show button (render children)
 * - Any other value (-, R, or missing) = hide button (return null)
 *
 * This component is style-agnostic - it only handles permission checks.
 * All styling, icons, loading states, etc. are handled by the children.
 *
 * Usage:
 * ```tsx
 * <PermissionAwareButton
 *   buttonType="delete"
 *   requestStage={requestStage}
 *   requestId={requestId}
 * >
 *   <TouchableOpacity
 *     style={styles.deleteButton}
 *     onPress={handleDelete}
 *     disabled={isDeleting}
 *   >
 *     <Trash2 size={18} color="#666" />
 *   </TouchableOpacity>
 * </PermissionAwareButton>
 * ```
 */
export const PermissionAwareButton: React.FC<PermissionAwareButtonProps> = ({
  buttonType,
  requestStage,
  requestId,
  children,
  loadingComponent,
  noPermissionComponent = null,
}) => {
  const permissions = useButtonPermissions(requestStage, requestId);
  const { isImpersonating, mode } = useImpersonation();

  // Read-only impersonation: all button types are mutation actions, hide them all
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';
  if (isReadOnlyImpersonation) {
    return <>{noPermissionComponent}</>;
  }

  // Show loading component if provided and still loading
  if (permissions.isLoading && loadingComponent) {
    return <>{loadingComponent}</>;
  }

  // Determine if button should be shown based on type
  let shouldShow = false;

  switch (buttonType) {
    case 'save':
      shouldShow = permissions.canShowSave;
      break;
    case 'edit':
      shouldShow = permissions.canShowEdit;
      break;
    case 'delete':
      shouldShow = permissions.canShowDelete;
      break;
    case 'submit':
      shouldShow = permissions.canShowSubmit;
      break;
    default:
      console.warn(`Unknown button type: ${buttonType}`);
      shouldShow = false;
  }

  // If no permission, show noPermissionComponent or null
  if (!shouldShow) {
    return <>{noPermissionComponent}</>;
  }

  // Render children if permission granted
  return <>{children}</>;
};
